/**
 * Независимая проверка пакета розыгрыша. Ничего не берёт на веру: всё пересчитывается
 * из исходных данных пакета теми же публичными правилами (формула индекса, расчёт выплат).
 *
 *  1. Подпись сервера (Ed25519) — пакет не изменён после выдачи.
 *  2. Обязательство: SHA-256(соль:параметры) совпадает с хешем, опубликованным до ставок;
 *     состав и базовые цены в параметрах совпадают с раскрытыми.
 *  3. Бары агрегата: каждый бар, показанный игрокам, пересчитывается из минутных баров акций.
 *  4. P₀, P₁, Δ — из пересчитанных баров.
 *  5. Хронология: ходы и изменения прогноза — до фиксации; состав раскрыт не раньше круга 3.
 *  6. Вклады игроков по кругам совпадают с проводками кредитов.
 *  7. Расчёт: банки, комиссия, выплаты и возвраты пересчитываются заново и сверяются.
 *  8. Проводки: выплаты, возвраты и комиссия совпадают с расчётом; банк розыгрыша закрыт в ноль.
 *
 * Результаты — коды сообщений с параметрами (messages.ts); русский текст строится из них же.
 */
import { verifyAsync } from '@noble/ed25519';
import { settle, type SettlementResult } from '@mt/game-core';
import { aggregateAt, formulaById, lookupFromArray, p0Value, roundToTick } from '@mt/market-index';
import { canonicalJson, fromBase64, sha256Hex, utf8 } from './canonical.ts';
import { note, noteRu, type CheckId, type VerifyCode, type VerifyNote, type VerifyParam } from './messages.ts';
import { PACKAGE_FORMAT, type Check, type CheckStatus, type SignedPackage, type VerificationPackage } from './types.ts';

export interface VerifyOptions {
  /** ожидаемый открытый ключ сервера (base64); если задан — сверяется с ключом в файле */
  trustedPublicKey?: string;
}

export interface VerifyReport {
  ok: boolean;
  checks: Check[];
  /** итог по-русски; summaryNote — код для перевода */
  summary: string;
  summaryNote: VerifyNote;
}

/** Проверка из заметок: заголовок и русский текст — из каталога. */
function check(id: CheckId, status: CheckStatus, notes: VerifyNote[]): Check {
  return { id, title: noteRu(note(`title.${id}`)), status, details: notes.map(noteRu), notes };
}

/** Накопитель строк результата. */
function lines() {
  const notes: VerifyNote[] = [];
  return { notes, add: (code: VerifyCode, params?: Record<string, VerifyParam>) => void notes.push(note(code, params)) };
}

export async function verifyPackage(file: unknown, opts: VerifyOptions = {}): Promise<VerifyReport> {
  const checks: Check[] = [];
  const add = (c: Check) => checks.push(c);

  const signed = file as SignedPackage;
  if (!signed || typeof signed !== 'object' || !signed.package || signed.package.format !== PACKAGE_FORMAT) {
    const summaryNote = note('summary.unrecognized');
    return { ok: false, checks: [check('format', 'fail', [note('format.notPackage')])], summary: noteRu(summaryNote), summaryNote };
  }
  const pkg = signed.package;

  add(await checkSignature(signed, opts));
  add(await checkCommitment(pkg));
  const bars = checkAggregate(pkg);
  add(bars.check);
  add(checkPrices(pkg, bars.recomputed));
  add(checkTimeline(pkg));
  add(checkContributions(pkg));
  const recomputed = checkSettlement(pkg);
  add(recomputed.check);
  add(checkLedger(pkg));

  const failed = checks.filter((c) => c.status === 'fail');
  const ok = failed.length === 0;
  const summaryNote = ok
    ? note(checks.some((c) => c.status === 'warn') ? 'summary.okWarn' : 'summary.ok', { ok: checks.filter((c) => c.status === 'ok').length, total: checks.length })
    : note('summary.fail', { titles: failed.map((c) => note(`title.${c.id}`)) });
  return { ok, checks, summary: noteRu(summaryNote), summaryNote };
}

async function checkSignature(signed: SignedPackage, opts: VerifyOptions): Promise<Check> {
  const fail = (code: VerifyCode, params?: Record<string, VerifyParam>) => check('signature', 'fail', [note(code, params)]);
  try {
    const s = signed.signature;
    if (!s || s.algorithm !== 'Ed25519') return fail('signature.missing');
    if (opts.trustedPublicKey && opts.trustedPublicKey !== s.publicKey) return fail('signature.keyMismatch');
    const ok = await verifyAsync(fromBase64(s.value), utf8(canonicalJson(signed.package)), fromBase64(s.publicKey));
    return ok
      ? check('signature', 'ok', [note(opts.trustedPublicKey ? 'signature.keyTrusted' : 'signature.keyUntrusted', { keyId: s.keyId }), note('signature.intact')])
      : fail('signature.invalid');
  } catch (e) {
    return fail('signature.error', { error: (e as Error).message });
  }
}

async function checkCommitment(pkg: VerificationPackage): Promise<Check> {
  const d = lines();
  let ok = true;
  const c = pkg.commitment;
  const h = await sha256Hex(`${c.salt}:${c.payload}`);
  if (h === c.hash) d.add('commitment.hashOk', { hash: h.slice(0, 16) });
  else ((ok = false), d.add('commitment.hashMismatch', { computed: h, published: c.hash }));
  let p: Record<string, unknown> = {};
  try {
    p = JSON.parse(c.payload);
  } catch {
    return check('commitment', 'fail', [note('commitment.badJson')]);
  }
  const symbols = pkg.index.components.map((x) => x.symbol);
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  if (!same(p.symbols, symbols)) ((ok = false), d.add('commitment.symbolsMismatch', { committed: JSON.stringify(p.symbols), revealed: JSON.stringify(symbols) }));
  else d.add('commitment.symbols', { symbols: symbols.join(', ') });
  if (!same(p.basePrices, pkg.index.components.map((x) => x.basePrice))) ((ok = false), d.add('commitment.basePricesMismatch'));
  else d.add('commitment.basePricesOk');
  if (p.p0MarketTime !== pkg.index.p0MarketTime) ((ok = false), d.add('commitment.p0Mismatch'));
  if (p.formula !== pkg.index.formula || p.indexBase !== pkg.index.indexBase || p.tick !== pkg.index.tick) ((ok = false), d.add('commitment.formulaMismatch'));
  if (p.sessionId !== pkg.index.sessionId) ((ok = false), d.add('commitment.sessionMismatch'));
  return check('commitment', ok ? 'ok' : 'fail', d.notes);
}

function checkAggregate(pkg: VerificationPackage) {
  const ix = pkg.index;
  const formula = formulaById(ix.formula);
  if (!formula || formula.components !== ix.components.length) {
    return { check: check('aggregate', 'fail', [note('aggregate.unknownFormula', { formula: ix.formula, n: ix.components.length })]), recomputed: new Map<number, { c: number }>() };
  }
  const lookups = ix.components.map((c) => lookupFromArray(c.bars));
  const params = {
    p0MarketTime: Date.parse(ix.p0MarketTime),
    startAt: pkg.timeline.startAt,
    minuteMs: pkg.timeline.minuteMs,
    basePrices: ix.components.map((c) => c.basePrice),
    avgVolumes: ix.components.map((c) => c.avgVolume),
  };
  const recomputed = new Map<number, { o: number; h: number; l: number; c: number; v: number }>();
  const mismatches: VerifyNote[] = [];
  for (const sb of ix.servedBars) {
    const r = aggregateAt(lookups, params, sb.k, formula);
    recomputed.set(sb.k, r);
    const diff = (['o', 'h', 'l', 'c', 'v'] as const).filter((f) => Math.abs(r[f] - sb[f]) > 1e-9);
    if (diff.length) {
      const fields = diff.map((f) => note('aggregate.field', { field: f, shown: String(sb[f]), recomputed: String(r[f]) }));
      mismatches.push(note('aggregate.barMismatch', { k: sb.k, time: sb.marketTime, fields }));
    }
  }
  const result: Check = mismatches.length
    ? check('aggregate', 'fail', mismatches.slice(0, 10))
    : check('aggregate', 'ok', [
        note('aggregate.ok', { n: ix.servedBars.length, formula: ix.formula }),
        note('aggregate.source', { source: pkg.game.sourceDescription, kind: pkg.game.source }), // kind — для подписи источника на языке клиента
      ]);
  return { check: result, recomputed };
}

function checkPrices(pkg: VerificationPackage, re: Map<number, { c: number }>): Check {
  const s = pkg.settlement;
  const d = lines();
  let ok = true;
  const p0 = re.get(-1)?.c;
  const formula = formulaById(pkg.index.formula);
  // P₀ по формуле: для индекса тройки — I₀ (5000), для открытого инструмента — его цена на баре P₀
  const base = formula ? p0Value(formula, pkg.index.components.map((c) => c.basePrice)) : NaN;
  if (p0 !== base || s.p0 !== base) ((ok = false), d.add('prices.p0Mismatch', { base, recomputed: String(p0), settled: String(s.p0) }));
  else d.add('prices.p0', { p0, time: pkg.index.p0MarketTime });
  const p1 = re.get(3)?.c ?? null;
  if (pkg.outcome.kind === 'market') {
    if (p1 === null) ((ok = false), d.add('prices.noTarget'));
    else {
      if (s.p1 !== p1) ((ok = false), d.add('prices.p1Mismatch', { settled: String(s.p1), recomputed: String(p1) }));
      const delta = roundToTick(p1 - base, pkg.index.tick);
      const ticks = Math.round(delta / pkg.index.tick);
      if (pkg.outcome.deltaTicks !== ticks) ((ok = false), d.add('prices.deltaMismatch', { settled: String(pkg.outcome.deltaTicks), recomputed: String(ticks) }));
      d.add('prices.p1', { p1, time: pkg.index.targetMarketTime, sign: delta > 0 ? '+' : delta < 0 ? '−' : '±', delta: Math.abs(delta) });
    }
  } else d.add('prices.notNeeded', { kind: pkg.outcome.kind });
  return check('prices', ok ? 'ok' : 'fail', d.notes);
}

function checkTimeline(pkg: VerificationPackage): Check {
  const t = pkg.timeline;
  const d = lines();
  let ok = true;
  const late = pkg.actions.filter((a) => a.kind === 'action' && a.at > t.lockAt + 1_000);
  if (late.length) ((ok = false), d.add('timeline.late', { actions: late.map((a) => `${a.name} ${a.action}`).join(', ') }));
  else d.add('timeline.allBefore', { n: pkg.actions.filter((a) => a.kind === 'action').length, time: new Date(t.lockAt).toISOString() });
  // раскрытие состава: по коду записи хронологии, в старых пакетах — по русскому тексту
  const comp = pkg.actions.find((a) => a.kind === 'reveal' && (a.msg ? a.msg.code === 'log.round3' : /состав/i.test(a.text)));
  if (comp) {
    if (comp.at < t.startAt + 2 * t.minuteMs - 1_000) ((ok = false), d.add('timeline.compositionEarly'));
    else d.add('timeline.compositionOk', { time: new Date(comp.at).toISOString() });
  }
  if (pkg.viewer) {
    const after = pkg.viewer.forecastHistory.filter((f) => f.at > t.lockAt + 1_000);
    if (after.length) ((ok = false), d.add('timeline.viewerLate', { n: after.length }));
    else d.add('timeline.viewerOk', { n: pkg.viewer.forecastHistory.length });
    const me = pkg.participants.find((p) => p.playerId === pkg.viewer!.playerId);
    const last = pkg.viewer.forecastHistory[pkg.viewer.forecastHistory.length - 1];
    if (me && last && (last.direction !== me.direction || last.points !== me.points)) ((ok = false), d.add('timeline.finalMismatch'));
  }
  const unordered = pkg.actions.some((a, i) => i > 0 && a.seq <= pkg.actions[i - 1]!.seq);
  if (unordered) ((ok = false), d.add('timeline.unordered'));
  return check('timeline', ok ? 'ok' : 'fail', d.notes);
}

function checkContributions(pkg: VerificationPackage): Check {
  const esc = `escrow:${pkg.game.gameId}`;
  const d = lines();
  let ok = true;
  for (const p of pkg.participants) {
    const acc = `player:${p.playerId}`;
    const byRound = [0, 0, 0];
    let ante = 0;
    for (const t of pkg.ledger) {
      const r = Math.max(1, t.round ?? 1) - 1;
      if (t.from === acc && t.to === esc) {
        if (t.kind === 'ante') ante += t.amount;
        else byRound[r] = (byRound[r] ?? 0) + t.amount;
      }
      if (t.from === esc && t.to === acc && t.kind === 'uncalled-return') byRound[r] = (byRound[r] ?? 0) - t.amount;
    }
    const same = ante === p.ante && byRound.every((v, i) => v === (p.roundBets[i] ?? 0));
    if (!same) ((ok = false), d.add('contributions.mismatch', { name: p.name, ante, rounds: byRound, settledAnte: p.ante, settledRounds: p.roundBets }));
  }
  if (ok) d.add('contributions.ok', { n: pkg.participants.length });
  return check('contributions', ok ? 'ok' : 'fail', d.notes);
}

function checkSettlement(pkg: VerificationPackage): { check: Check; result: SettlementResult | null } {
  const seats = [...pkg.participants]
    .sort((a, b) => a.order - b.order)
    .map((p) => ({ playerId: p.playerId, order: p.order, ante: p.ante, roundBets: p.roundBets, folded: p.folded, direction: p.direction, pointsTicks: p.pointsTicks }));
  let r: SettlementResult;
  try {
    r = settle(seats, pkg.outcome, pkg.rules);
  } catch (e) {
    return { check: check('settlement', 'fail', [note('settlement.error', { error: (e as Error).message })]), result: null };
  }
  const s = pkg.settlement;
  const d = lines();
  let ok = true;
  if (r.status !== s.status) ((ok = false), d.add('settlement.statusMismatch', { settled: s.status, recomputed: r.status }));
  for (const rp of r.players) {
    const sp = s.players.find((x) => x.playerId === rp.playerId);
    if (!sp) {
      ok = false;
      d.add('settlement.noPlayer', { playerId: rp.playerId });
      continue;
    }
    if (sp.payout !== rp.payout || sp.refund !== rp.refund || sp.contributed !== rp.contributed) {
      ok = false;
      d.add('settlement.playerMismatch', { name: sp.name, payout: sp.payout, refund: sp.refund, rePayout: rp.payout, reRefund: rp.refund });
    }
  }
  const commIn = s.commissionByRound.map((x) => x.commission).join(',');
  const commRe = r.commissionByRound.map((x) => x.commission).join(',');
  if (commIn !== commRe) ((ok = false), d.add('settlement.commissionMismatch', { settled: commIn, recomputed: commRe }));
  if (s.bankDelta !== r.bankWithheld) ((ok = false), d.add('settlement.bankMismatch', { settled: s.bankDelta, recomputed: r.bankWithheld }));
  if (!r.totals.balanced) ((ok = false), d.add('settlement.unbalanced'));
  if (ok) {
    const kind = note(r.kind === 'market' ? 'settlement.kind.market' : r.kind === 'walkover' ? 'settlement.kind.walkover' : 'settlement.kind.cancel');
    // причина — кодом сервера (переводится на клиенте), в старых пакетах без кода — русским текстом
    const reason: VerifyParam | null = r.reasonMsg ? { srv: r.reasonMsg } : r.reason;
    if (reason) d.add('settlement.outcomeReason', { kind, reason });
    else d.add('settlement.outcome', { kind });
    d.add('settlement.totals', { pots: r.pots.length, commission: r.commissionByRound.map((x) => x.commission), withheld: r.bankWithheld });
    for (const p of r.players.filter((x) => x.payout > 0)) {
      const name = s.players.find((x) => x.playerId === p.playerId)?.name ?? String(undefined);
      if (p.errorTicks !== null) d.add('settlement.payoutError', { name, payout: p.payout, error: p.errorTicks * pkg.rules.tick });
      else d.add('settlement.payout', { name, payout: p.payout });
    }
    if (r.kind === 'market' && r.players.some((p) => p.payout > 0 && !p.correctDirection)) ((ok = false), d.add('settlement.wrongDirection'));
  }
  return { check: check('settlement', ok ? 'ok' : 'fail', d.notes), result: r };
}

function checkLedger(pkg: VerificationPackage): Check {
  const esc = `escrow:${pkg.game.gameId}`;
  const s = pkg.settlement;
  const d = lines();
  let ok = true;
  let net = 0;
  for (const t of pkg.ledger) {
    if (t.to === esc) net += t.amount;
    if (t.from === esc) net -= t.amount;
  }
  if (net !== 0) ((ok = false), d.add('ledger.escrowNonZero', { net }));
  else d.add('ledger.escrowZero');
  const sum = (kind: string, to?: string) => pkg.ledger.filter((t) => t.from === esc && t.kind === kind && (!to || t.to === to)).reduce((a, t) => a + t.amount, 0);
  for (const p of s.players) {
    const acc = `player:${p.playerId}`;
    if (sum('payout', acc) !== p.payout || sum('refund', acc) !== p.refund) {
      ok = false;
      d.add('ledger.playerMismatch', { name: p.name, payout: sum('payout', acc), refund: sum('refund', acc), settledPayout: p.payout, settledRefund: p.refund });
    }
  }
  const commission = sum('commission', 'house:bank');
  if (commission !== s.bankDelta) ((ok = false), d.add('ledger.commissionMismatch', { ledger: commission, settled: s.bankDelta }));
  if (ok) d.add('ledger.ok', { debited: s.totals.debited, paid: s.totals.paid, refunded: s.totals.refunded, bank: s.totals.bank });
  return check('ledger', ok ? 'ok' : 'fail', d.notes);
}
